import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { buildEmployeeDetailEditProfileRoute } from "../../routes/AdminRoutes";
// Material UI
import { Box, Button, Avatar, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

// Custom Components
import Table from "../../components/Table/Table";
import usePermissions from "../../redux/selectors/usePermissions";

// User Detail Components
import Roles from "./Roles";
import PasswordResetButton from "./PasswordResetButton";
import AdministrationInfoLayout from "../../components/Layouts/AdministrationInfoLayout";
import EmployeeAverages from "./EmployeeAverages";

const useStyles = makeStyles(() => ({
  profile: {
    width: 124,
    height: 124
  }
}));

const Datum = ({ label, value }) => (
  <Box display="flex" justifyContent="space-between">
    <Typography variant="body2" noWrap sx={{ pr: 1, flex: "0 1 40%" }}>
      {label}
    </Typography>
    <Typography
      variant="subtitle2"
      noWrap
      sx={{ pr: { xs: 0, md: 3 }, mr: { xs: "12.5%", lg: "25%" }, flex: 1 }}
    >
      {value ? value : "-"}
    </Typography>
  </Box>
);

const Employee = ({ employeeID, employee, isLoading, history }) => {
  const { profile } = useStyles();
  const { t } = useTranslation();
  const { allowPasswordReset } = usePermissions();
  const isAdmin = useSelector(
    state => state.userState.user.permissions.isAdmin
  );
  const navigateToEditProfile = React.useCallback(() => {
    history.push(buildEmployeeDetailEditProfileRoute(employeeID));
  }, [history.push]);

  const columns = [
    {
      title: t("employeeDetail.name"),
      field: "name"
    },
    {
      title: t("employeeDetail.role"),
      field: "role"
    }
  ];

  return (
    <AdministrationInfoLayout
      name={employee?.fullName || ""}
      image={
        <Avatar
          alt={employee?.fullName || ""}
          src={employee?.profileImage || ""}
          className={profile}
        />
      }
      subtitle1={`${employee?.position?.name || ""} | ${employee?.location
        ?.name || ""}`}
      subtitle2={`${t("employeeDetail.lastLogin")}: ${employee?.lastLogin ||
        ""}`}
      compliance={employee?.compliance || 0}
      completedTraining={employee?.completedTraining || 0}
      trainingHours={employee?.trainingHours || 0}
      averageScore={employee?.userAvg || 0}
      averages={<EmployeeAverages employeeId={employeeID} />}
      roles={
        isAdmin ? (
          <Roles
            employeeID={employeeID}
            instructorID={employee?.instructor?.id}
          />
        ) : null
      }
      infoActions={
        <Button
          color="primary"
          variant="contained"
          onClick={navigateToEditProfile}
        >
          {t("employeeDetail.editProfile")}
        </Button>
      }
      infoSection={
        <>
          <Box display="flex" flexDirection="column" flex={1} minWidth={260}>
            <Datum
              label={t("employeeDetail.employeeID")}
              value={employee?.employeeId || ""}
            />
            <Datum
              label={t("employeeDetail.hireDate")}
              value={employee?.hireDate || ""}
            />
            <Datum
              label={t("employeeDetail.phone")}
              value={employee?.phone || ""}
            />
            <Datum
              label={t("employeeDetail.email")}
              value={employee?.email || ""}
            />
          </Box>
          <Box
            display="flex"
            flexDirection={{ xs: "column", md: "row" }}
            flex={1}
            alignItems={{ xs: "stretch", md: "flex-start" }}
            flexWrap="wrap"
          >
            <Box
              display="flex"
              flexDirection="column"
              flex={1}
              mb={3}
              minWidth={260}
            >
              <Datum
                label={t("employeeDetail.username")}
                value={employee?.username || ""}
              />
              <Datum label={t("employeeDetail.password")} value={"******"} />
            </Box>
            {allowPasswordReset && (
              <PasswordResetButton
                email={employee?.email}
                emailBlocked={employee?.emailBlocked}
                disabled={!employee?.canSendPasswordReset}
                employeeID={employeeID}
              />
            )}
          </Box>
        </>
      }
      tables={[
        {
          header: t("employeeDetail.groupsHeader"),
          table: (
            <Table
              itemType="record"
              columns={columns}
              data={employee?.groupRoles || []}
              hidePagination={true}
            />
          )
        }
      ]}
      isLoadingInfo={isLoading}
      isLoadingRoles={isLoading}
    />
  );
};

export default Employee;
